/**
 * TeamList — table of org users with their current per-app grants.
 *
 * Click any row to open the AccessEditorDrawer for that user.
 * Search filters across name / email / department.
 */
import React, { useMemo, useState } from 'react';

const ROLE_LABELS = {
  superuser:  'Access Administrator',
  superadmin: 'Platform Admin',
  admin:      'Compliance Officer',
  associate:  'Associate',
  supervisor: 'Supervisor',
  lead:       'Lead',
  viewer:     'Observer',
};

function effectiveRole(user) {
  if (user.role === 'admin' && user.department_id) return 'Department Admin';
  return ROLE_LABELS[user.role] || user.role;
}

function formatRel(d) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return min < 1 ? 'just now' : `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toISOString().slice(0, 10);
}

export default function TeamList({ users, apps, onEditUser }) {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(term) ||
      (u.email || '').toLowerCase().includes(term) ||
      (u.department_name || '').toLowerCase().includes(term)
    );
  }, [users, q]);

  return (
    <div>
      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, or department…"
          style={{
            width: '100%', maxWidth: 480,
            padding: '8px 12px', fontSize: 13,
            border: '1px solid #cbd5e1', borderRadius: 8,
            background: '#ffffff',
          }}
        />
      </div>

      {/* Table */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1.2fr 1fr 2fr 100px',
          gap: 16, padding: '10px 18px',
          background: '#fafbfc', borderBottom: '1px solid #e2e8f0',
          fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: '#94a3b8', fontFamily: 'ui-monospace, monospace',
        }}>
          <div>Person</div>
          <div>Role</div>
          <div>Department</div>
          <div>Apps</div>
          <div style={{ textAlign: 'right' }}>Last seen</div>
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: '24px', color: '#94a3b8', fontSize: 13 }}>
            No users match this search.
          </div>
        )}
        {filtered.map(u => {
          const activeGrants = (u.access || []).filter(g => g.active);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => onEditUser(u)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1.2fr 1fr 2fr 100px',
                gap: 16, padding: '14px 18px',
                width: '100%', textAlign: 'left',
                background: '#ffffff', border: 0,
                borderTop: '1px solid #f1f5f9',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fafbfc'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
            >
              <div>
                <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 500 }}>{u.name || '(no name)'}</div>
                <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace, monospace', marginTop: 2 }}>{u.email}</div>
              </div>
              <div style={{ fontSize: 13, color: '#0f172a' }}>{effectiveRole(u)}</div>
              <div style={{ fontSize: 13, color: u.department_name ? '#0f172a' : '#94a3b8' }}>
                {u.department_name || 'no department'}
              </div>
              <div>
                {activeGrants.length === 0 ? (
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {activeGrants.slice(0, 8).map(g => {
                      const app = apps.find(a => a.key === g.app_key);
                      const isViewer = g.access_role === 'viewer';
                      const isTemp = g.expires_at;
                      return (
                        <span
                          key={g.id}
                          title={`${app?.label || g.app_key} (${g.access_role}${isTemp ? `, expires ${formatRel(g.expires_at)}` : ''})`}
                          style={{
                            fontSize: 11, padding: '2px 8px',
                            background: isViewer ? '#f1f5f9' : '#eff6ff',
                            color: isViewer ? '#475569' : '#1e3a8a',
                            border: `1px solid ${isViewer ? '#cbd5e1' : '#bfdbfe'}`,
                            borderRadius: 999,
                            fontFamily: 'ui-monospace, monospace',
                          }}
                        >
                          {app?.label || g.app_key}
                          {isTemp && <span style={{ marginLeft: 4, opacity: 0.65 }}>·</span>}
                        </span>
                      );
                    })}
                    {activeGrants.length > 8 && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>+{activeGrants.length - 8} more</span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>
                {u.last_login_at ? formatRel(u.last_login_at) : '—'}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
